import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { avisoCorrecto, avisoInformativo } from "../../components/AvisosMensajes/MensajesSwalf";
import { guardarVoucher } from "../../../actions/pagosActions";
import { updateStateModalBasic, updateStateModalLarge } from "../../../actions/modalActions";

const AddNewVoucherModal = ({ onClose, id_dt_matricula_proyecto, tp_cuota }) => {
  const dispatch = useDispatch();
  const [experienceFile, setExperienceFile] = useState(null);
  const [salesFile, setSalesFile] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const { usuario } = useSelector((state) => state.usuario);

  const expInputRef = useRef(null);
  const salesInputRef = useRef(null);

  const handleFiles = (fileList, type) => {
    const filesArray = Array.from(fileList);
    const file = filesArray[0]; // solo tomamos el primer archivo

    if (!file) return;

    if(file.size > 10 * 1024 * 1024) {
      avisoInformativo('El archivo no debe superar los 5MB de tamaño.');
      return;
    }

    if(!["image/jpeg", "image/png"].includes(file.type)) {
      avisoInformativo('Formato de archivo no permitido. Solo se aceptan imágenes (JPG/PNG)');
      return;
    }

    if (type === "experience") {
      if (selectedType === "sales" && salesFile) {
        avisoInformativo(
          "Solo puede subir un voucher a la vez. Si desea cambiar el voucher de experiencia, primero elimine el voucher de ventas."
        );
        return;
      }

      // Si sube uno en experiencia, limpiamos ventas
      setExperienceFile(file);
      setSalesFile(null);
      setSelectedType("experience");
    } else if (type === "sales") {
      if (selectedType === "experience" && experienceFile) {
        avisoInformativo(
          "Solo puede subir un voucher a la vez. Si desea cambiar el voucher de ventas, primero elimine el voucher de experiencia."
        );
        return;
      }
      // Si sube uno en ventas, limpiamos experiencia
      setSalesFile(file);
      setExperienceFile(null);
      setSelectedType("sales");
    }
  };

  const handleFileInputChange = (e, type) => {
    handleFiles(e.target.files, type);
    // muy importante: limpiar el value para que pueda volver a subir el mismo archivo después
    e.target.value = "";
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files, type);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemoveFile = (type) => {
    if (type === "experience") {
      setExperienceFile(null);
    } else if (type === "sales") {
      setSalesFile(null);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData();

    const data = {
      id_dt_matricula_proyecto: id_dt_matricula_proyecto,
      tipo_voucher: selectedType,
      id_usuario: usuario.id_usuario,
      tp_cuota: tp_cuota
    }
    formData.append("data", JSON.stringify(data));
    
    if (experienceFile) {
      formData.append("archivo", experienceFile);
    }
    if (salesFile) {
      formData.append("archivo", salesFile);
    }
    

    dispatch(guardarVoucher(formData)).then((response) => {

      dispatch(updateStateModalLarge());
    
    });

    if (onClose) onClose();
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="container-fluid py-2">
        {/* Encabezado */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">
              <i className="bi bi-receipt-cutoff me-2 text-primary" />
              Subir voucher de pago
            </h5>
            <small className="text-muted">
              ID:{" "}
              <span className="fw-bold text-primary">{id_dt_matricula_proyecto}</span>
            </small>
          </div>

          <span className="badge bg-light text-secondary border">
            <i className="bi bi-info-circle me-1" />
            Solo se permite un voucher (experiencia o ventas) a la vez
          </span>
        </div>

        {/* Aviso regla negocio */}
        <div className="alert alert-primary py-2 mb-4">
          <div className="d-flex align-items-center">
            <i className="bi bi-shield-check me-2" />
            <div className="small">
              Selecciona o arrastra un voucher en una de las secciones. Si subes
              en
              <strong> experiencia</strong>, se limpiará el de
              <strong> ventas</strong>, y viceversa.
            </div>
          </div>
        </div>

        <div className="row g-3">
          {/* Columna Experiencia */}
          <div className="col-12 col-md-6">
            <div className="card shadow-sm h-100 border-0">
              <div className="card-header d-flex justify-content-between align-items-center py-2 bg-white border-0 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <span className="avatar rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center">
                    <i className="bi bi-mortarboard text-primary" />
                  </span>
                  <div>
                    <h6 className="mb-0">Voucher de Experiencia</h6>
                    <small className="text-muted">
                      Comprobantes asociados al area de experiencia
                    </small>
                  </div>
                </div>

                {experienceFile && (
                  <span className="badge bg-success bg-opacity-10 text-success">
                    <i className="bi bi-check-circle me-1" />
                    Archivo cargado
                  </span>
                )}
              </div>

              <div className="card-body d-flex flex-column">
                {/* Zona de arrastre */}
                <div
                  className={`rounded-4 p-3 mb-3 text-center position-relative 
                ${experienceFile ? "bg-success bg-opacity-10" : "bg-light"}`}
                  style={{
                    border: "2px dashed",
                    borderColor: experienceFile ? "#19875433" : "#6c757d66",
                    cursor: "pointer",
                    minHeight: "170px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() => expInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, "experience")}
                >
                  <div className="mb-2">
                    <i className="bi bi-cloud-arrow-up fs-1" />
                  </div>
                  <p className="mb-1 fw-semibold">
                    Arrastra y suelta el voucher aquí
                  </p>
                  <small className="text-muted d-block mb-1">
                    o haz clic para seleccionar un archivo
                  </small>
                  <small className="text-muted">
                    Formatos permitidos: imágenes (JPG/PNG) o PDF
                  </small>

                  <input
                    ref={expInputRef}
                    type="file"
                    className="d-none"
                    multiple
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileInputChange(e, "experience")}
                  />
                </div>

                {/* Lista de archivos */}
                {experienceFile && (
                  <div className="mt-auto">
                    <p className="mb-2 fw-semibold small text-uppercase text-muted">
                      Archivo seleccionado
                    </p>
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item px-2 border-0 rounded-3 bg-light d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center flex-grow-1">
                          <i className="bi bi-file-earmark-text me-2 text-primary" />
                          <div className="text-start">
                            <div
                              className="text-truncate small fw-semibold"
                              title={experienceFile.name}
                            >
                              {experienceFile.name}
                            </div>
                            <small className="text-muted">
                              {(experienceFile.size / 1024).toFixed(1)} KB
                            </small>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={() => handleRemoveFile("experience")}
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Columna Ventas */}
          <div className="col-12 col-md-6">
            <div className="card shadow-sm h-100 border-0">
              <div className="card-header d-flex justify-content-between align-items-center py-2 bg-white border-0 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <span className="avatar rounded-circle bg-warning bg-opacity-10 d-flex align-items-center justify-content-center">
                    <i className="bi bi-cash-stack text-warning" />
                  </span>
                  <div>
                    <h6 className="mb-0">Voucher de Ventas</h6>
                    <small className="text-muted">
                      Comprobantes asociados al area de Ventas
                    </small>
                  </div>
                </div>

                {salesFile && (
                  <span className="badge bg-success bg-opacity-10 text-success">
                    <i className="bi bi-check-circle me-1" />
                    Archivo cargado
                  </span>
                )}
              </div>

              <div className="card-body d-flex flex-column">
                {/* Zona de arrastre */}
                <div
                  className={`rounded-4 p-3 mb-3 text-center position-relative 
                ${salesFile ? "bg-success bg-opacity-10" : "bg-light"}`}
                  style={{
                    border: "2px dashed",
                    borderColor: salesFile ? "#19875433" : "#6c757d66",
                    cursor: "pointer",
                    minHeight: "170px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() => salesInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, "sales")}
                >
                  <div className="mb-2">
                    <i className="bi bi-cloud-arrow-up fs-1" />
                  </div>
                  <p className="mb-1 fw-semibold">
                    Arrastra y suelta el voucher aquí
                  </p>
                  <small className="text-muted d-block mb-1">
                    o haz clic para seleccionar un archivo
                  </small>
                  <small className="text-muted">
                    Formatos permitidos: imágenes (JPG/PNG) o PDF
                  </small>

                  <input
                    ref={salesInputRef}
                    type="file"
                    className="d-none"
                    multiple
                    accept="image/*,.pdf"
                    onChange={(e) => handleFileInputChange(e, "sales")}
                  />
                </div>

                {/* Lista de archivos */}
                {salesFile && (
                  <div className="mt-auto">
                    <p className="mb-2 fw-semibold small text-uppercase text-muted">
                      Archivo seleccionado
                    </p>
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item px-2 border-0 rounded-3 bg-light d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center flex-grow-1">
                          <i className="bi bi-file-earmark-text me-2 text-primary" />
                          <div className="text-start">
                            <div
                              className="text-truncate small fw-semibold"
                              title={salesFile.name}
                            >
                              {salesFile.name}
                            </div>
                            <small className="text-muted">
                              {(salesFile.size / 1024).toFixed(1)} KB
                            </small>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={() => handleRemoveFile("sales")}
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer de acciones */}
        <div className="mt-4 d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2">
          <div className="small text-muted">
            <i className="bi bi-exclamation-circle me-1" />
            Recuerda que el voucher debe ser legible y corresponder al registro
            de pago seleccionado.
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={experienceFile === null && salesFile === null}
            >
              <i className="bi bi-save me-1" />
              Guardar vouchers
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

AddNewVoucherModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  idRegistroPago: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AddNewVoucherModal;
